/**
 * Sub-pass 2B Item 2 — Persistent PostHog owner/admin exclusion.
 *
 * When a browser has ever been used to authenticate as a server-trusted
 * owner/admin (``user.is_admin === true`` from ``/auth/me``), we plant
 * a versioned local marker. From that point on:
 *
 *   * ``initPostHog()`` short-circuits and never loads the SDK.
 *   * If PostHog was already initialized on that page load, we
 *     immediately stop autocapture, session recording, pageviews, and
 *     any queued events (see ``stopLiveTracking()``).
 *   * Logout does NOT clear the marker.
 *   * Consent changes must NOT remove or override the marker.
 *
 * A brand-new browser / cleared storage / incognito profile has no
 * marker and cannot be identified as the owner until the owner
 * authenticates there once (documented, accepted limitation).
 */

// Bump the version suffix if the semantics ever change so old markers
// are not silently reused.
const MARKER_KEY = "stockaily_owner_excluded_v1";
const MARKER_VALUE = "1";

function safeStorage() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch (_) {
    return null;
  }
}

/** Return true when this browser is permanently excluded from PostHog. */
export function isOwnerExcluded() {
  const ls = safeStorage();
  if (!ls) return false;
  try {
    return ls.getItem(MARKER_KEY) === MARKER_VALUE;
  } catch (_) {
    return false;
  }
}

/**
 * Fully stop an already-initialized PostHog instance.
 *
 * Order is critical: ``reset()`` clears cached data AND restores the
 * SDK's *default* consent state — which can silently undo an
 * immediately preceding ``opt_out_capturing()``. We therefore call
 * ``reset()`` FIRST, then invoke ``opt_out_capturing()`` as the
 * authoritative final state.
 *
 * We also disable autocapture, page-view auto-tracking, and session
 * recording explicitly — belt-and-suspenders in case a queued event
 * or a pending flush hasn't drained yet.
 */
export function stopLiveTracking(posthog) {
  if (!posthog) return;
  // 1. Stop session recording if it was running (safe no-op otherwise).
  try {
    if (typeof posthog.stopSessionRecording === "function") {
      posthog.stopSessionRecording();
    } else if (posthog.sessionRecording && typeof posthog.sessionRecording.stopRecording === "function") {
      posthog.sessionRecording.stopRecording();
    }
  } catch (_) { /* noop */ }
  // 2. Disable autocapture and page-view auto-tracking via runtime config.
  try {
    if (typeof posthog.set_config === "function") {
      posthog.set_config({
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        disable_session_recording: true,
        rageclick: false,
      });
    }
  } catch (_) { /* noop */ }
  // 3. Reset cached data / stored consent — MUST happen BEFORE the
  //    final opt-out because reset() restores the SDK's default
  //    consent state and could otherwise re-enable capture.
  try { posthog.reset?.(true); } catch (_) { /* noop */ }
  // 4. Authoritative final state: opt out. This wins any race with a
  //    queued or in-flight event.
  try { posthog.opt_out_capturing?.(); } catch (_) { /* noop */ }
}

/**
 * Plant the exclusion marker. Idempotent. Never throws.
 * Optionally halts an already-initialized PostHog instance in the
 * correct order (see ``stopLiveTracking``).
 */
export function markOwnerExcluded(posthogInstance) {
  const ls = safeStorage();
  if (ls) {
    try { ls.setItem(MARKER_KEY, MARKER_VALUE); } catch (_) { /* storage disabled */ }
  }
  if (posthogInstance) stopLiveTracking(posthogInstance);
}

/**
 * Consent-safe hook. Called from AuthContext whenever the resolved
 * user changes. If ``user.is_admin === true`` (server-trusted), we
 * plant the marker AND fully stop any live PostHog instance.
 *
 * ``getPosthog`` is optional and defers the analytics-module import
 * so this helper can stay side-effect-free at import time (the
 * analytics module is only fetched when PostHog is actually running).
 */
export async function applyOwnerExclusionIfNeeded(user, getPosthog) {
  if (!user || user.is_admin !== true) return false;
  // Plant the marker FIRST so any concurrent init() call sees it and
  // refuses to load the SDK.
  markOwnerExcluded();
  if (typeof getPosthog === "function") {
    try {
      const posthog = await getPosthog();
      if (posthog) stopLiveTracking(posthog);
    } catch (_) { /* analytics not loaded */ }
  }
  return true;
}

// Exposed for tests.
export const __MARKER_KEY = MARKER_KEY;
